import * as Sentry from '@sentry/react';
import {updateWebflowJson} from 'Common/api';
import {useDebouncedCallback} from 'Hooks/useDebouncedCallback';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {v4 as uuidv4} from 'uuid';
import {AstView} from 'rendition-core';
import {useUserContext} from 'Contexts/UserContext/UserContext';
import {sendClickedCopyWebflowJSON} from 'slack/events';
import {copyToClipBoard} from '../../utils/CopyHelpers';

export interface WebflowControls {
    webflowJson: string;
    webflowError: boolean;
    webflowLoading: boolean;
    copyWebflowJson: () => void;
    setWebflowJson: React.Dispatch<React.SetStateAction<string>>;
    setWebflowError: React.Dispatch<React.SetStateAction<boolean>>;
    setWebflowLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useWebflowJSON(view: AstView | null, id: string, active?: boolean): WebflowControls {
    const [webflowJson, setWebflowJson] = useState<string>('');
    const [webflowError, setWebflowError] = useState(false);
    const [webflowLoading, setWebflowLoading] = useState(false);
    const latestAPICall = useRef();
    const {sessionId, userId} = useUserContext();

    const doUpdateWebflowJson = useCallback(async () => {
        if (view) {
            const callUUID = uuidv4();
            latestAPICall.current = callUUID;
            try {
                setWebflowError(false);
                const result = await updateWebflowJson(id, view);
                const json = result.data.webflowJson;

                if (latestAPICall.current === callUUID) {
                    setWebflowJson(json);
                    setWebflowLoading(false);
                }
            } catch (e) {
                Sentry.captureException(e);
                if (latestAPICall.current === callUUID) {
                    setWebflowLoading(false);
                    setWebflowError(true);
                }
            }
        }
    }, [id, view]);

    const autoUpdateWebflow = useDebouncedCallback(doUpdateWebflowJson, 750);

    useEffect(() => {
        if (active && view) {
            setWebflowLoading(true);
            autoUpdateWebflow();
        }
    }, [active, autoUpdateWebflow, view]);

    const copyWebflowJson = useCallback(() => {
        setWebflowError(false);
        if (!webflowJson) {
            return;
        }
        copyToClipBoard(webflowJson, 'application/json');
        sendClickedCopyWebflowJSON(sessionId, userId, id);
    }, [id, sessionId, userId, webflowJson]);

    /*
    const convertForWebFlow = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const text = await navigator.clipboard.readText();
        function convertHandler(event: ClipboardEvent) {
            event.clipboardData?.setData('application/json', text);
            event.preventDefault();
            document.removeEventListener('copy', convertHandler, true);
        }
        document.addEventListener('copy', convertHandler, true);
        document.execCommand('copy');
    };
    */

    const webflowControls = useMemo(
        () => ({
            webflowJson,
            webflowError,
            webflowLoading,
            copyWebflowJson,
            setWebflowJson,
            setWebflowError,
            setWebflowLoading,
        }),
        [copyWebflowJson, webflowError, webflowJson, webflowLoading]
    );

    return webflowControls;
}
